import { useCallback, useState } from 'react';
import type { ReactNode } from 'react';

import { Box } from '@opendoor/atoms/box';
import { cn } from '@opendoor/atoms/utils';
import { getBoundsFromShape } from 'components/marketplace/map/utils';
import { MAPBOX_ACCESS_TOKEN, MAPBOX_MAP_CONFIG, MAPBOX_MAP_STYLE } from 'helpers/globals';
import { GeolocateControl, Map, NavigationControl } from 'react-map-gl/mapbox';
import type { MapMouseEvent, MapRef, ViewStateChangeEvent } from 'react-map-gl/mapbox';

import 'mapbox-gl/dist/mapbox-gl.css';

import { marketplaceMarkerAddressIdAtPoint } from './mapMarkerLayer';
import type { MarketShape } from './types';

// Mapbox popup overrides and the popup fade-in keyframe. Scoped to
// a local `.mapRoot` class (Next.js CSS Modules require at least one local
// selector per rule) and applied below via `className={styles.mapRoot}`. The
// keyframe inside the module is intentionally global so `MapMarker.tsx` can
// reference it by name in an inline `animation` declaration.
import styles from './MarketplaceMap.module.css';

const MIN_ZOOM = 7;
const MAX_ZOOM = 17;
const MAP_STYLE = { height: '100%', width: '100%' } as const;

interface MarketplaceMapProps {
  children?: ReactNode;
  marketShape: MarketShape;
  onClick: () => void;
  onMoveEnd: (event: ViewStateChangeEvent) => void;
  mapRef: React.RefObject<MapRef>;
}

export const MarketplaceMap = ({
  children,
  marketShape,
  onClick,
  onMoveEnd,
  mapRef,
}: MarketplaceMapProps) => {
  const [cursor, setCursor] = useState<'pointer' | 'grab' | 'grabbing'>('grab');

  const handleMouseEnter = useCallback(() => setCursor('pointer'), []);
  const handleMouseLeave = useCallback(() => setCursor('grab'), []);
  const handleMouseDown = useCallback(() => setCursor('grabbing'), []);
  const handleMouseUp = useCallback(() => setCursor('grab'), []);
  const handleClick = useCallback(
    (event: MapMouseEvent) => {
      if (!marketplaceMarkerAddressIdAtPoint({ event, map: event.target })) {
        onClick();
      }
    },
    [onClick],
  );

  const [initialViewState] = useState(() => {
    try {
      return { bounds: getBoundsFromShape(marketShape) };
    } catch {
      return { longitude: 0, latitude: 0, zoom: MIN_ZOOM };
    }
  });

  // Wrapper carries the CSS Module class that scopes the Mapbox popup +
  // marker overrides. We cannot put the class directly on <Map> because
  // react-map-gl does not forward `className` to the root container.
  return (
    <Box className={cn(styles.mapRoot, 'h-screen w-full')}>
      <Map
        ref={mapRef}
        mapboxAccessToken={MAPBOX_ACCESS_TOKEN}
        mapStyle={MAPBOX_MAP_STYLE}
        config={MAPBOX_MAP_CONFIG}
        onMoveEnd={onMoveEnd}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        cursor={cursor}
        style={MAP_STYLE}
        minZoom={MIN_ZOOM}
        maxZoom={MAX_ZOOM}
        initialViewState={initialViewState}
        fadeDuration={0}
        onClick={handleClick}
        reuseMaps
      >
        <NavigationControl position="top-left" showCompass={false} />
        <GeolocateControl position="top-right" />
        {children}
      </Map>
    </Box>
  );
};
